import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiGet, apiPost, isUnauthorizedApiError, SUPER_TOKEN_KEY, TENANT_TOKEN_KEY } from '../../api/client';
import {
  superOrgBtn,
  superOrgBtnOutline,
  superOrgField,
  superOrgInput,
} from './superOrgPanels';
import type { OrgRow } from './superOrgTypes';

export function SuperOrgsPage() {
  const nav = useNavigate();
  const token = localStorage.getItem(SUPER_TOKEN_KEY);
  const [orgs, setOrgs] = useState<OrgRow[]>([]);
  const [err, setErr] = useState('');
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [isDemo, setIsDemo] = useState(false);

  const load = useCallback(async () => {
    if (!token) return;
    setErr('');
    try {
      const data = await apiGet<OrgRow[]>('/api/super/organizations', token);
      setOrgs(data);
    } catch (e) {
      if (isUnauthorizedApiError(e)) {
        localStorage.removeItem(SUPER_TOKEN_KEY);
        nav('/super/login', { replace: true });
        return;
      }
      setErr((e as Error).message);
    }
  }, [token, nav]);

  useEffect(() => {
    if (!token) {
      nav('/super/login', { replace: true });
      return;
    }
    void load();
  }, [token, nav, load]);

  const createOrg = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    setErr('');
    try {
      await apiPost(
        '/api/super/organizations',
        { name: name.trim(), slug: slug.trim() || undefined, isDemo },
        token,
        { successMessage: 'Empresa creada' }
      );
      setName('');
      setSlug('');
      setIsDemo(false);
      void load();
    } catch (ex) {
      setErr((ex as Error).message);
    }
  };

  const impersonate = async (orgId: string) => {
    if (!token) return;
    setErr('');
    try {
      const data = await apiPost<{ token: string; user: { email: string }; organization: { name: string; slug: string } }>(
        `/api/super/organizations/${orgId}/impersonate`,
        {},
        token
      );
      localStorage.setItem(TENANT_TOKEN_KEY, data.token);
      window.open('/app/agents', '_blank');
    } catch (ex) {
      setErr((ex as Error).message);
    }
  };

  if (!token) return null;

  return (
    <div style={{ flex: 1, minHeight: 0, background: '#f8fafc' }}>
      <div style={{ maxWidth: 900, margin: '0 auto', padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <h1 style={{ fontSize: 22, margin: '0 0 8px' }}>Empresas</h1>
          <button type="button" style={superOrgBtn} onClick={() => nav('/super/gastos')}>
            Gasto total
          </button>
        </div>
        <p style={{ color: '#64748b', marginBottom: 24 }}>
          Creá organizaciones y usuarios que luego entran en{' '}
          <Link to="/login" style={{ color: '#7c3aed' }}>
            /login
          </Link>{' '}
          con el slug de la empresa. Abrí el detalle de una empresa para gestionar usuarios, consumo y modelos.
        </p>

        <section
          style={{
            background: '#fff',
            borderRadius: 12,
            padding: 20,
            border: '1px solid #e2e8f0',
            marginBottom: 24,
          }}
        >
          <h2 style={{ fontSize: 16, margin: '0 0 12px' }}>Nueva empresa</h2>
          <form onSubmit={createOrg} style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
            <label style={superOrgField}>
              Nombre
              <input style={superOrgInput} value={name} onChange={(e) => setName(e.target.value)} required />
            </label>
            <label style={superOrgField}>
              Slug (opcional)
              <input
                style={superOrgInput}
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder="auto desde nombre"
              />
            </label>
            <label style={{ ...superOrgField, flexDirection: 'row', alignItems: 'center', gap: 8, paddingBottom: 8 }}>
              <input
                type="checkbox"
                checked={isDemo}
                onChange={(e) => setIsDemo(e.target.checked)}
              />
              Empresa de demo
            </label>
            <button type="submit" style={superOrgBtn}>
              Crear
            </button>
          </form>
        </section>

        {err ? <p style={{ color: '#b91c1c' }}>{err}</p> : null}

        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {orgs.map((o) => (
            <li
              key={o.id}
              style={{
                background: '#fff',
                border: '1px solid #e2e8f0',
                borderRadius: 10,
                padding: 16,
                marginBottom: 12,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, alignItems: 'flex-start' }}>
                <div>
                  <strong>{o.name}</strong>
                  <span style={{ color: '#64748b', marginLeft: 8 }}>/{o.slug}</span>
                  {o.isDemo ?
                    <span
                      style={{
                        marginLeft: 8,
                        fontSize: 11,
                        fontWeight: 700,
                        padding: '2px 8px',
                        borderRadius: 999,
                        background: '#ecfdf5',
                        color: '#047857',
                        border: '1px solid #a7f3d0',
                        verticalAlign: 'middle',
                      }}
                    >
                      Demo
                    </span>
                  : null}
                  <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 6 }}>
                    Usuarios: {o.counts.users} · Agentes: {o.counts.agents} · Conversaciones: {o.counts.conversations}
                  </div>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    style={{ ...superOrgBtn, background: '#7c3aed', color: '#fff', textDecoration: 'none', fontFamily: 'inherit' }}
                    onClick={() => void impersonate(o.id)}
                  >
                    Impersonar
                  </button>
                  <button
                    type="button"
                    style={{ ...superOrgBtn, textDecoration: 'none', fontFamily: 'inherit' }}
                    onClick={() => nav(`/super/orgs/${o.id}/usuarios?nuevo=1`)}
                  >
                    Crear usuario
                  </button>
                  <button
                    type="button"
                    style={{ ...superOrgBtnOutline, textDecoration: 'none', fontFamily: 'inherit' }}
                    onClick={() => nav(`/super/orgs/${o.id}/usuarios`)}
                  >
                    Ver detalle
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
